import Button from '@ingka/button';
import InputField from '@ingka/input-field';
import Loading from '@ingka/loading';
import Select, { Option } from '@ingka/select';
import SSRIcon from '@ingka/ssr-icon';
import cloudPath from '@ingka/ssr-icon/paths/cloud';
import Text from '@ingka/text';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchFiles } from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';
import AppHeader from '@/components/layout/AppHeader';
import { useApp } from '@/context/AppContext';
import type { FilesQueryParams, UploadRecord } from '@/types';
import { entityName, fmtDate, fmtSize, fmtTime, productName } from '@/utils/formatters';
import styles from './ArchivePage.module.css';

export default function ArchivePage() {
  const { user, catalog } = useApp();
  const navigate = useNavigate();

  const [items, setItems] = useState<UploadRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const load = useCallback(async (params: FilesQueryParams) => {
    setLoading(true);
    try {
      const res = await fetchFiles(params);
      setItems(res.items);
      setTotal(res.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({
      q,
      product: product || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      pageSize,
    });
  }, [q, product, from, to, page, load]);

  const clearFilters = () => {
    setQ('');
    setProduct('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const hasFilters = !!(q || product || from || to);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className={styles.page}>
      <AppHeader />

      <main className={styles.main}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(user ? '/upload/product' : '/')}
        >
          ← Back
        </button>

        <div className={styles.hero}>
          <Text tagName="h2" textStyle="Heading.XL" className={styles.heroTitle}>
            {user ? 'Your files' : 'File archive'}
          </Text>
          <Text tagName="p" textStyle="Body.L" className={styles.heroBody}>
            {user
              ? "Every file you've uploaded. Search by name or ID, filter by date or product."
              : 'Search every upload by file name or ID. Filter by date or product.'}
          </Text>
        </div>

        {/* ─── Filter bar ─────────────────────────────────────────────────── */}
        <div className={styles.filterBar}>
          <div className={styles.filterGrid}>
            <InputField
              id="archive-search"
              type="text"
              label="Search file name or ID"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. calculations or ploffline"
            />
            <InputField
              id="archive-from"
              type="date"
              label="From date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
            <InputField
              id="archive-to"
              type="date"
              label="To date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
            <Select
              id="archive-product"
              label="Product"
              value={product}
              onChange={(e) => {
                setProduct(e.target.value);
                setPage(1);
              }}
            >
              <Option value="" name="All products" />
              {(catalog?.products ?? []).map((p) => (
                <Option key={p.id} value={p.id} name={p.name} />
              ))}
            </Select>
          </div>

          {hasFilters && (
            <div className={styles.filterFooter}>
              <Text tagName="p" textStyle="Body.S" className={styles.filterCount}>
                Showing <strong>{items.length}</strong> of {total} files
              </Text>
              <Button type="tertiary" size="small" text="Clear filters" onClick={clearFilters} />
            </div>
          )}
        </div>

        {/* ─── Table ──────────────────────────────────────────────────────── */}
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div>ID</div>
            <div>File</div>
            <div>Product / Type</div>
            <div>Uploaded</div>
            <div>Size</div>
            <div>Status</div>
          </div>

          {loading && (
            <div className={styles.loadingWrap}>
              <Loading text="Loading files…" />
            </div>
          )}

          {!loading && items.length === 0 && !hasFilters && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <SSRIcon paths={cloudPath} style={{ width: 40, height: 40 }} />
              </div>
              <p className={styles.emptyTitle}>No uploads yet</p>
              <p className={styles.emptyBody}>Files you upload will appear here.</p>
              {user && (
                <Button
                  type="emphasised"
                  text="Upload a file"
                  onClick={() => navigate('/upload/product')}
                />
              )}
            </div>
          )}

          {!loading && items.length === 0 && hasFilters && (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>No files match your search</p>
              <p className={styles.emptyBody}>
                Try widening the date range or clearing your filters.
              </p>
            </div>
          )}

          {!loading &&
            items.map((u, i) => (
              <div
                key={u.id}
                className={`${styles.row}${i < items.length - 1 ? ` ${styles.rowBorder}` : ''}`}
              >
                <div className={styles.rowId}>{u.id}</div>

                <div className={styles.rowFile}>
                  <div className={styles.rowFileIcon}>📄</div>
                  <span className={styles.rowFileName}>{u.fileName}</span>
                </div>

                <div>
                  <div className={styles.rowProduct}>{productName(catalog, u.product)}</div>
                  <div className={styles.rowEntity}>{entityName(catalog, u.product, u.entity)}</div>
                </div>

                <div>
                  <div className={styles.rowDate}>{fmtDate(u.uploadedAt)}</div>
                  <div className={styles.rowTime}>{fmtTime(u.uploadedAt)}</div>
                </div>

                <div className={styles.rowSize}>{fmtSize(u.sizeBytes)}</div>

                <div>
                  <span
                    className={`${styles.statusBadge}${u.status === 'completed' ? ` ${styles.statusBadgeOk}` : ''}`}
                  >
                    {u.status === 'completed' ? '✓ OK' : u.status}
                  </span>
                </div>
              </div>
            ))}
        </div>

        {/* ─── Pagination ─────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Button
              type="secondary"
              size="small"
              text="Previous"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Text tagName="span" textStyle="Body.S" className={styles.paginationLabel}>
              Page {page} of {totalPages}
            </Text>
            <Button
              type="secondary"
              size="small"
              text="Next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
